import React, { useState } from "react";
import { FileText } from "lucide-react";
import { orderPdfProxyUrl } from "../services/ejagritiApi";

// Zigzag hearing timeline for e-Jagriti (Consumer Court) cases. Accepts either
// the case-history `timeline` shape (date / before / courtNumber / documents) or
// the case-status `historyAndOrders` shape (dateOfHearing / caseStage /
// dailyOrder...). PDFs open through the backend proxy.
const openDoc = (pdfUrl) => {
  const u = orderPdfProxyUrl(pdfUrl);
  if (u) window.open(u, "_blank", "noopener,noreferrer");
};

export default function ConsumerTimeline({ timeline = [], initialCount = 4 }) {
  const [showAll, setShowAll] = useState(false);

  if (!timeline.length) {
    return <p className="text-white/50 text-sm text-center py-8">No timeline events found.</p>;
  }

  const visible = showAll ? timeline : timeline.slice(0, initialCount);
  const hasMore = timeline.length > initialCount;

  return (
    <div>
      {/* Cards + center spine live in their own relative box so the spine never
          runs through the View All button below it. */}
      <div className="relative">
        {/* Center divider spine — darker white + wider */}
        <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-1 rounded-full bg-white/40 md:-translate-x-1/2" />
        <div className="space-y-3">
          {visible.map((t, i) => {
            const right = i % 2 === 1;
            const daily = t.documents?.dailyOrder || t.dailyOrder;
            const judg = t.documents?.judgement || t.judgement;
            const proceedings = t.documents?.courtroomProceedings || t.courtroomProceedings;
            return (
              <div key={t.id ?? i} className={`relative md:flex ${right ? "md:justify-end" : "md:justify-start"}`}>
                <div className="ml-8 md:ml-0 md:w-[46%]">
                  <div className="relative bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                    <div className="flex">
                      {/* HEARING tab */}
                      <div
                        className="bg-blue-600 text-white text-[9px] font-bold tracking-widest flex items-center justify-center px-1"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                      >
                        {(t.type || "HEARING").toUpperCase()}
                      </div>
                      <div className="flex-1 p-3">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-white font-bold text-sm">{t.date || t.dateOfHearing || "—"}</span>
                          {t.courtNumber != null && (
                            <span className="text-white/50 text-[11px]">Court Number: {t.courtNumber}</span>
                          )}
                        </div>
                        {t.before && (
                          <p className="text-white/70 text-xs mt-0.5">
                            Before: <span className="text-white">{t.before}</span>
                          </p>
                        )}
                        <div className="flex justify-between items-center mt-2 flex-wrap gap-2">
                          <span className="text-xs">
                            Status: <span className="text-yellow-400 font-semibold">{t.displayLabel || t.status || t.caseStage || "—"}</span>
                          </span>
                          {daily?.available && daily?.pdfUrl && (
                            <button
                              onClick={() => openDoc(daily.pdfUrl)}
                              className="inline-flex items-center gap-1 bg-yellow-500 hover:bg-yellow-400 text-[#060125] text-[11px] font-bold rounded px-2.5 py-1"
                            >
                              <FileText className="h-3 w-3" /> VIEW DAILY ORDER
                            </button>
                          )}
                        </div>
                        {(judg?.available && judg?.pdfUrl) || (proceedings?.available && proceedings?.pdfUrl) ? (
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {judg?.available && judg?.pdfUrl && (
                              <button
                                onClick={() => openDoc(judg.pdfUrl)}
                                className="inline-flex items-center gap-1 border border-blue-400/40 text-blue-300 text-[11px] font-semibold rounded px-2.5 py-0.5 hover:bg-blue-500/15"
                              >
                                <FileText className="h-3 w-3" /> View Judgement
                              </button>
                            )}
                            {proceedings?.available && proceedings?.pdfUrl && (
                              <button
                                onClick={() => openDoc(proceedings.pdfUrl)}
                                className="inline-flex items-center gap-1 border border-white/30 text-white/90 text-[11px] font-semibold rounded px-2.5 py-0.5 hover:bg-white/10"
                              >
                                <FileText className="h-3 w-3" /> Proceedings
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {hasMore && (
        <div className="flex justify-end mt-5">
          <button
            onClick={() => setShowAll((s) => !s)}
            className="px-5 py-2 rounded-full border border-white/20 text-white/90 hover:bg-white/10 text-sm font-semibold transition-colors"
          >
            {showAll ? "Show Less" : `View All (${timeline.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
